//_pira__

import styled from '@emotion/styled'
import React, { useEffect } from 'react'
import { Platform } from '@/firebase/firestore/Platform'
import { Sex } from '@/firebase/firestore/Sex'
import { VoiceChat } from '@/firebase/firestore/VoiceChats'
import Image from 'next/image'
import fitty from 'fitty'
import { useLocale } from '@/hooks/useLocale'

type Props = {
  targetId: string
  previewImage: string
  templateColor: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7
  name: string
  id: string
  sex: Sex
  platform: Array<Platform>
  voiceChat: Array<VoiceChat>
  playStyle: string
  fontColor: string
  fontFamily: string
  playTimeWeekDayBegin: string
  playTimeWeekDayEnd: string
  playTimeWeekEndBegin: string
  playTimeWeekEndEnd: string
  rank: number
  rules: number[]
  udemae: string
  baito: string
  favoriteWeapon: string
  hitokoto: string
}

export const Splatoon3_6: React.FC<Props> = ({
  targetId,
  previewImage,
  fontColor,
  fontFamily,
  templateColor,
  name,
  sex,
  voiceChat,
  rank,
  playStyle,
  rules,
  udemae,
  baito,
  hitokoto,
}) => {
  const { t } = useLocale()
  useEffect(() => {
    fitty('#name', {
      maxSize: 16,
      minSize: 12,
    })
    fitty('#id', {
      maxSize: 16,
      minSize: 8,
    })
    fitty('#rules', {
      maxSize: 16,
      minSize: 10,
    })
    fitty('#udemae', {
      maxSize: 18,
      minSize: 12,
    })
    fitty('#baito', {
      maxSize: 18,
      minSize: 12,
    })
    fitty('#hitokoto', {
      maxSize: 18,
      minSize: 12,
    })
  }, [])
  return (
    <Container id={targetId} fontFamily={fontFamily} fontColor={fontColor}>
      <Image
        width="1800"
        height="1050"
        priority={true}
        unoptimized={true}
        src={`/static/images/profiles/splatoon3_6/${templateColor}.jpeg`}
        alt={`Splatoon3自己紹介カード ${templateColor}`}
      />
      <PreviewImage src={previewImage} />
      <Name>
        <span id="name">{name}</span>
      </Name>
      <SexText>
        {sex == 0 && ' ♂ '}
        {sex == 1 && ' ♀ '}
      </SexText>
      {voiceChat.includes(3) && <VC0 />}
      {voiceChat.includes(5) && <VC1 />}
      {voiceChat.includes(1) && <VC2 />}
      {voiceChat.includes(100) && <VCCaseBy />}
      {(voiceChat.includes(3) ||
        voiceChat.includes(5) ||
        voiceChat.includes(1)) && <VCOk />}
      {voiceChat.includes(4) && <VCNg />}
      <Rank>
        <span id={'rank'}>{rank}</span>
      </Rank>
      {playStyle === 'beginner' && <PlayStyleBeginner />}
      {playStyle === 'serious' && <PlayStyleSerious />}
      {playStyle === 'enjoy' && <PlayStyleEnjoy />}
      {rules.includes(0) && <Rule0 />}
      {rules.includes(1) && <Rule1 />}
      {rules.includes(2) && <Rule2 />}
      {rules.includes(3) && <Rule3 />}
      {rules.includes(4) && <Rule4 />}
      {rules.includes(5) && <Rule5 />}
      <UdemaeText>
        <span id={'udemae'}>{udemae}</span>
      </UdemaeText>
      <BaitoText>
        <span id={'baito'}>{baito}</span>
      </BaitoText>
      <Hitokoto>
        <span id={'hitokoto'}>{hitokoto}</span>
      </Hitokoto>
    </Container>
  )
}

const Container = styled.div<{ fontFamily: string; fontColor: string }>`
  position: relative;
  width: 100%;
  line-height: 0;
  * {
    line-height: 1;
    font-family: ${({ fontFamily }) => fontFamily};
    color: ${({ fontColor }) => fontColor};
    border-color: ${({ fontColor }) => fontColor};
  }
`

const PreviewImage = styled.img`
  position: absolute;
  top: 28%;
  left: 75.5%;
  width: 75px;
  height: 70px;
  object-fit: cover;
  border-radius: 8px;
  overflow: hidden;
`

const Name = styled.div`
  position: absolute;
  top: 27%;
  left: 7%;
  width: 18%;
  height: 14%;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    letter-spacing: -2px;
    font-weight: bold;
  }
`

const SexText = styled.div`
  position: absolute;
  top: 30%;
  left: 32%;
  font-size: 20px;
`

const Rank = styled.span`
  position: absolute;
  top: 30%;
  left: 38%;
  text-align: center;
  width: 18%;
  font-size: 20px;
  font-weight: bold;
  word-break: break-all;
  line-height: 13px;
`

const PlayStyleBeginner = styled.div`
  position: absolute;
  top: 29%;
  left: 59.5%;
  border: solid 2px;
  border-radius: 50%;
  width: 28px;
  height: 28px;
`

const PlayStyleSerious = styled.div`
  position: absolute;
  top: 39.5%;
  left: 59.5%;
  border: solid 2px;
  border-radius: 12px;
  width: 30px;
  height: 20px;
`

const PlayStyleEnjoy = styled.div`
  position: absolute;
  top: 48%;
  left: 57.8%;
  border: solid 2px;
  border-radius: 12px;
  width: 44px;
  height: 20px;
`

const VCBase = styled.div`
  position: absolute;
  top: 78%;
  left: 33%;
  border: solid 3px;
  border-radius: 50%;
  width: 26px;
  height: 26px;
`

const VC0 = styled(VCBase)``

const VC1 = styled(VCBase)`
  left: 38.5%;
`

const VC2 = styled(VCBase)`
  left: 44%;
`

const VCCaseBy = styled(VCBase)`
  position: absolute;
  top: 72.2%;
  left: 42%;
  border: solid 2px;
  border-radius: 12px;
  width: 34px;
  height: 18px;
`

const VCOk = styled.div`
  position: absolute;
  top: 72.2%;
  left: 33%;
  border: solid 2px;
  border-radius: 12px;
  width: 18px;
  height: 18px;
`

const VCNg = styled.div`
  position: absolute;
  top: 72.2%;
  left: 37.8%;
  border: solid 2px;
  border-radius: 12px;
  width: 18px;
  height: 18px;
`

const UdemaeText = styled.span`
  position: absolute;
  top: 53%;
  left: 27%;
  width: 18%;
  height: 12%;
  text-align: center;
  font-weight: bold;
  word-break: break-all;
  line-height: 13px;
`

const BaitoText = styled.span`
  position: absolute;
  top: 53%;
  left: 37%;
  width: 18%;
  height: 12%;
  text-align: center;
  font-weight: bold;
  word-break: break-all;
  line-height: 13px;
`

const RuleBase = styled.div`
  position: absolute;
  top: 50%;
  left: 9%;
  width: 32px;
  height: 32px;
  border: solid 3px;
  border-radius: 50%;
`

const Rule0 = styled(RuleBase)``

const Rule1 = styled(RuleBase)`
  left: 18%;
`

const Rule2 = styled(RuleBase)`
  top: 63%;
  left: 9%;
`

const Rule3 = styled(RuleBase)`
  top: 63%;
  left: 18%;
`

const Rule4 = styled(RuleBase)`
  top: 75.5%;
  left: 9%;
`

const Rule5 = styled(RuleBase)`
  top: 75.5%;
  left: 18%;
`

const Hitokoto = styled.span`
  position: absolute;
  top: 72%;
  left: 60%;
  width: 30%;
  height: 24%;
  text-align: center;
  font-weight: bold;
  word-break: break-all;
  line-height: 13px;
`
